import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';

const card = {
  background: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  padding: '14px',
  boxShadow: '0 1px 4px rgba(15,23,42,0.05)'
};

const label = { fontSize: '11px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' };
const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' };

function Pill({ tone = 'slate', children }) {
  const tones = {
    green: ['#dcfce7', '#166534', '#86efac'],
    amber: ['#fef3c7', '#92400e', '#fbbf24'],
    red: ['#fee2e2', '#991b1b', '#fca5a5'],
    blue: ['#dbeafe', '#1e40af', '#93c5fd'],
    slate: ['#f1f5f9', '#334155', '#cbd5e1'],
  };
  const [bg, color, border] = tones[tone] || tones.slate;
  return <span style={{ background: bg, color, border: `1px solid ${border}`, borderRadius: '999px', padding: '4px 8px', fontSize: '11px', fontWeight: 800 }}>{children}</span>;
}

function Stat({ title, value, hint, tone }) {
  const colors = { green: '#15803d', amber: '#b45309', red: '#dc2626', blue: '#2563eb', slate: '#334155' };
  return (
    <div style={card}>
      <div style={label}>{title}</div>
      <div style={{ color: colors[tone] || colors.slate, fontSize: '24px', fontWeight: 900, marginTop: '6px' }}>{value}</div>
      {hint && <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>{hint}</div>}
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <div style={{ ...card, marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
        <h2 style={{ margin: 0, color: '#0f172a', fontSize: '16px' }}>{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function SystemPage() {
  const [inventory, setInventory] = useState(null);
  const [selfcheck, setSelfcheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [r, s] = await Promise.all([api.get('/admin/inventory'), api.get('/admin/selfcheck')]);
      setInventory(r.data);
      setSelfcheck(s.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Systemdaten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }

  async function runRepair() {
    setBusy('repair');
    setMessage('');
    setError('');
    try {
      await api.post('/admin/repair/migrate');
      setMessage('Migrationen wurden gepr??ft und angewendet.');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Repair fehlgeschlagen');
    } finally {
      setBusy('');
    }
  }

  async function downloadStatusReport() {
    setBusy('status-report');
    setMessage('');
    setError('');
    try {
      const r = await api.get('/admin/status-report.md', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/markdown;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'finanzanalyse-statusbericht.md';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setMessage('Statusbericht wurde erstellt.');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Statusbericht konnte nicht erstellt werden');
    } finally {
      setBusy('');
    }
  }

  async function runReanalysis() {
    setBusy('reanalyse');
    setMessage('');
    setError('');
    try {
      const r = await api.post('/reanalyse');
      setMessage(`${r.data.aktualisiert || 0} Buchungen wurden reanalysiert.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Reanalyse fehlgeschlagen');
    } finally {
      setBusy('');
    }
  }

  const health = inventory?.health;
  const tableRows = useMemo(() => {
    const tables = health?.database?.tables || {};
    return Object.entries(tables).map(([name, info]) => ({
      name,
      ok: info.exists && info.missingColumns.length === 0,
      missing: info.missingColumns
    }));
  }, [health]);

  const statusTone = health?.status === 'ok' ? 'green' : health ? 'amber' : 'red';
  const stats = health?.database?.stats || {};

  return (
    <div>
      <div style={{ marginBottom: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', color: '#0f172a' }}>Systemkonsole</h1>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px' }}>Health, Datenbank, Imports und Reparaturwerkzeuge</p>
          </div>
          <Pill tone={statusTone}>{health?.status || 'lädt'}</Pill>
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px', fontWeight: 700 }}>{error}</div>}
      {message && <div style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px', fontWeight: 700 }}>{message}</div>}

      {loading && (
        <div style={card}>
          <div style={{ color: '#64748b' }}>Systemdaten werden geladen...</div>
        </div>
      )}

      {!loading && health && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px', marginBottom: '12px' }}>
            <Stat title="Buchungen" value={stats.buchungen ?? 0} hint="gespeicherte Transaktionen" tone="blue" />
            <Stat title="Imports" value={stats.imports ?? 0} hint="Importläufe in der Historie" tone="slate" />
            <Stat title="Regeln" value={stats.regeln ?? 0} hint="aktive und gelernte Regeln" tone="green" />
            <Stat title="KI offen" value={stats.unbestaetigt ?? 0} hint="unbestätigte Vorschläge" tone={(stats.unbestaetigt ?? 0) > 0 ? 'amber' : 'green'} />
          </div>

          {selfcheck && (
            <Section title="Betriebs-Selbstcheck" action={<Pill tone={selfcheck.status === 'ok' ? 'green' : 'amber'}>{selfcheck.status}</Pill>}>
              <div style={{ display: 'grid', gap: '8px' }}>
                {selfcheck.checks?.map(c => (
                  <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' }}>
                    <div>
                      <div style={{ color: '#0f172a', fontWeight: 800, fontSize: '13px' }}>{c.label}</div>
                      <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>{c.detail}</div>
                    </div>
                    <Pill tone={c.ok ? 'green' : 'amber'}>{c.ok ? 'ok' : 'hinweis'}</Pill>
                  </div>
                ))}
                {selfcheck.warnings?.length > 0 && (
                  <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '9px', padding: '10px', color: '#92400e', fontSize: '12px', fontWeight: 700 }}>
                    {selfcheck.warnings.join(' ')}
                  </div>
                )}
              </div>
            </Section>
          )}

          <Section
            title="Schnellaktionen"
            action={<button onClick={load} style={buttonStyle('#0f172a')} disabled={!!busy}>Aktualisieren</button>}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <button onClick={runRepair} disabled={!!busy} style={buttonStyle('#2563eb')}>
                {busy === 'repair' ? 'Prüfe...' : 'Migrationen reparieren'}
              </button>
              <button onClick={runReanalysis} disabled={!!busy} style={buttonStyle('#16a34a')}>
                {busy === 'reanalyse' ? 'Analysiere...' : 'Regeln neu anwenden'}
              </button>
              <button onClick={downloadStatusReport} disabled={!!busy} style={buttonStyle('#0f766e')}>
                {busy === 'status-report' ? 'Erstelle...' : 'Statusbericht laden'}
              </button>
            </div>
          </Section>

          <Section title="Datenbankstruktur">
            <div style={{ display: 'grid', gap: '8px' }}>
              {tableRows.map(t => (
                <div key={t.name} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' }}>
                  <div>
                    <div style={{ ...mono, fontWeight: 800, color: '#0f172a', fontSize: '13px' }}>{t.name}</div>
                    <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>
                      {t.ok ? 'vollständig' : `fehlt: ${t.missing.join(', ')}`}
                    </div>
                  </div>
                  <Pill tone={t.ok ? 'green' : 'red'}>{t.ok ? 'ok' : 'fehlt'}</Pill>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Speicher und KI">
            <div style={{ display: 'grid', gap: '8px' }}>
              <InfoRow name="Version" value={health.version} />
              <InfoRow name="Zeit" value={new Date(health.time).toLocaleString('de-DE')} />
              <InfoRow name="Datenbank" value={health.database.path} />
              <InfoRow name="Uploads" value={`${health.uploads.path} (${health.uploads.writable ? 'beschreibbar' : 'nicht beschreibbar'})`} tone={health.uploads.writable ? 'green' : 'red'} />
              <InfoRow name="KI-Key" value={health.ai.configured ? 'konfiguriert' : 'fehlt'} tone={health.ai.configured ? 'green' : 'amber'} />
            </div>
          </Section>

          <Section title="Banken">
            {inventory.banks?.length ? inventory.banks.map(b => (
              <div key={b.bank} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#334155', fontWeight: 700 }}>{b.bank}</span>
                <span style={{ color: '#2563eb', fontWeight: 900 }}>{b.anzahl}</span>
              </div>
            )) : <Empty text="Noch keine Banken erkannt." />}
          </Section>

          <Section title="Letzte Imports">
            {inventory.imports?.length ? inventory.imports.slice(0, 6).map(i => (
              <div key={i.id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <strong style={{ color: '#0f172a', fontSize: '13px' }}>{i.dateiname || 'Import'}</strong>
                  <span style={{ color: '#16a34a', fontWeight: 900 }}>{i.anzahl}</span>
                </div>
                <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>
                  {i.bank || 'Unbekannt'} ?? {i.von_datum || '?'} bis {i.bis_datum || '?'} ?? {new Date(i.created_at).toLocaleString('de-DE')}
                </div>
              </div>
            )) : <Empty text="Noch keine Imports." />}
          </Section>

          <Section title="Systemereignisse">
            {inventory.events?.length ? inventory.events.slice(0, 8).map(e => (
              <div key={e.id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <strong style={{ color: '#0f172a', fontSize: '13px' }}>{e.typ}</strong>
                  <span style={{ color: '#94a3b8', fontSize: '11px' }}>{new Date(e.created_at).toLocaleString('de-DE')}</span>
                </div>
                <div style={{ color: '#475569', fontSize: '12px', marginTop: '2px' }}>{e.nachricht}</div>
              </div>
            )) : <Empty text="Noch keine Systemereignisse." />}
          </Section>
        </>
      )}
    </div>
  );
}

function buttonStyle(bg) {
  return {
    background: bg,
    color: 'white',
    border: 'none',
    borderRadius: '9px',
    padding: '10px 12px',
    cursor: 'pointer',
    fontWeight: 900,
    fontSize: '12px'
  };
}

function InfoRow({ name, value, tone = 'slate' }) {
  const pillTone = tone === 'green' ? 'green' : tone === 'red' ? 'red' : tone === 'amber' ? 'amber' : 'slate';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '92px 1fr', gap: '8px', alignItems: 'start', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' }}>
      <div style={label}>{name}</div>
      <div style={{ color: '#334155', fontSize: '12px', wordBreak: 'break-word' }}>
        {tone === 'slate' ? value : <Pill tone={pillTone}>{value}</Pill>}
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div style={{ color: '#94a3b8', textAlign: 'center', padding: '18px 8px', fontSize: '13px' }}>{text}</div>;
}
